import { mapFormAddressToCapitalGlobalProposalAddress } from '@/features/proposals/components/capital-global/utils/map-form-address-to-capital-global-proposal-address.util';
import type { AttachQuotePayload } from '@/services/proposals/types';
import type { CapitalGlobalProposalStepDadosDoSeguroValues } from '@/features/proposals/schemas/capital-global/proposal-step-dados-do-seguro.schema';
import { normalizeEmail } from '@/shared/utils/normalize-email.util';

function toDateOnly(value?: string) {
  return value ? value.split('T')[0] || value : '';
}

/**
 * Endereço de cada subestipulante. Linhas totalmente vazias são omitidas — mandar um
 * endereço em branco faria o back gravar nada (o writer só copia campo preenchido) e
 * poluiria o payload.
 */
function buildSubStipulatorAddresses(
  values: CapitalGlobalProposalStepDadosDoSeguroValues
) {
  return values.subStipulatorAddresses
    .filter(
      entry =>
        entry.cep.trim() ||
        entry.street.trim() ||
        entry.phone.trim() ||
        entry.email.trim()
    )
    .map(entry => ({
      customerPk: entry.customerPk,
      documentNumber: entry.documentNumber,
      sequenceNumber: entry.sequenceNumber,
      address: {
        zipCode: entry.cep,
        street: entry.street,
        streetNumber: entry.number,
        complement: entry.complement,
        neighborhood: entry.neighborhood,
        city: entry.city,
        state: entry.state,
      },
      phone: entry.phone,
      email: normalizeEmail(entry.email),
      // CAEPF (pessoa física): sexo e data de nascimento do sub para o i4Pro.
      ...(entry.gender ? { genderCode: entry.gender as 'M' | 'F' } : {}),
      ...(entry.dateOfBirth ? { dateOfBirth: entry.dateOfBirth } : {}),
    }));
}

export function buildCapitalGlobalStepDadosDoSeguroAttachPayload(
  values: CapitalGlobalProposalStepDadosDoSeguroValues,
  options: {
    terminateDate: string;
    proponentName: string;
  }
): Partial<AttachQuotePayload> {
  const terminate =
    options.terminateDate || toDateOnly(values.effectiveStartDate);

  const payload: Partial<AttachQuotePayload> = {
    effectiveDate: values.effectiveStartDate,
    terminateDate: terminate,
    customer: {
      pk: values.customerPk,
      socialName: '',
      cellphone: values.stipulatorPhone,
      email: normalizeEmail(values.stipulatorEmail),
      // CAEPF (pessoa física): data de nascimento e sexo do titular para o i4Pro.
      // `genderCode` (não `gender`) é o campo aceito pela DTO de attach; o back grava
      // `GenderCode` a partir dele e o mapper de pessoa do i4Pro usa `genderCode`.
      ...(values.dateOfBirth ? { dateOfBirth: values.dateOfBirth } : {}),
      ...(values.gender ? { genderCode: values.gender as 'M' | 'F' } : {}),
    },
    registrationAddress: mapFormAddressToCapitalGlobalProposalAddress(
      values,
      'registration'
    ),
    correspondenceSameAsRegistration: values.sameCorrespondenceAsRegistration,
    inspectionContacts: [
      {
        name: options.proponentName,
        email: normalizeEmail(values.stipulatorEmail),
        phone: values.stipulatorPhone.replace(/\D/g, ''),
      },
    ],
  };

  if (!values.sameCorrespondenceAsRegistration) {
    payload.correspondenceAddress = mapFormAddressToCapitalGlobalProposalAddress(
      values,
      'correspondence'
    );
  }

  const subStipulatorAddresses = buildSubStipulatorAddresses(values);
  if (subStipulatorAddresses.length) {
    payload.subStipulatorAddresses = subStipulatorAddresses;
  }

  return payload;
}
